// ============================================================
//  WHITEBOARD — Web app component
// ============================================================

import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import { exportWhiteboardToPdf } from '../lib/exportBoard.js'
import { uploadWhiteboardExport, FRIENDLY_TRY_AGAIN, isAppCheckText } from '../lib/api.js'

// ── Mini video tile ─────────────────────────────────────────
function MiniVideoTile({ stream, mirrored, label }) {
  const ref = useRef(null)
  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream || null
  }, [stream])
  if (!stream) return null
  return (
    <div className="wb-video-tile">
      <video ref={ref} autoPlay playsInline muted={mirrored}
        style={mirrored ? { transform: 'scaleX(-1)' } : undefined} />
      {label && <span className="wb-video-label">{label}</span>}
    </div>
  )
}

// ── Session timer ────────────────────────────────────────────
function SessionTimer({ timeLimitSeconds, callStartedAtMs }) {
  const [remaining, setRemaining] = useState(null)
  useEffect(() => {
    if (!timeLimitSeconds || !callStartedAtMs) return
    const tick = () => {
      const elapsed = Math.floor((Date.now() - callStartedAtMs) / 1000)
      setRemaining(Math.max(0, timeLimitSeconds - elapsed))
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [timeLimitSeconds, callStartedAtMs])
  if (remaining === null) return null
  const mins = Math.floor(remaining / 60)
  const secs = remaining % 60
  const low  = remaining <= 5 * 60
  return (
    <div className={`wb-session-timer ${low ? 'low' : ''}`}>
      {mins}:{String(secs).padStart(2, '0')}
    </div>
  )
}

// ── Constants ────────────────────────────────────────────────
const TOOLS = [
  { id: 'pen',    icon: '✏️' },
  { id: 'eraser', icon: '🧹' },
  { id: 'line',   icon: '📏' },
  { id: 'rect',   icon: '⬜' },
  { id: 'circle', icon: '⭕' },
  { id: 'arrow',  icon: '➡️' },
  { id: 'text',   icon: '🔤' },
]
const COLORS          = ['#1A1A1A', '#E24B4A', '#378ADD', '#639922', '#BA7517', '#D4537E']
const WIDTHS          = [2, 4, 8]
const ERASE_RADIUS_BASE = 10
const ACTIVE_TIMEOUT_MS = 30_000 // 30s — after this, activity is considered stale

const newId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

// ── Geometry helpers ─────────────────────────────────────────
function distToSegment(p, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y
  const lenSq = dx * dx + dy * dy
  if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y)
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy))
}

function objectNearPoint(obj, point, radius) {
  const g = obj.geometry
  if (g.kind === 'freehand') {
    for (let i = 0; i < g.points.length - 1; i++) {
      if (distToSegment(point, g.points[i], g.points[i + 1]) <= radius) return true
    }
    return g.points.length === 1 &&
      Math.hypot(point.x - g.points[0].x, point.y - g.points[0].y) <= radius
  }
  if (g.kind === 'shape') {
    const corners = [
      { x: g.start.x, y: g.start.y }, { x: g.end.x, y: g.start.y },
      { x: g.end.x,   y: g.end.y   }, { x: g.start.x, y: g.end.y },
    ]
    for (let i = 0; i < 4; i++) {
      if (distToSegment(point, corners[i], corners[(i + 1) % 4]) <= radius) return true
    }
    return false
  }
  if (g.kind === 'text') {
    return Math.hypot(point.x - g.anchor.x, point.y - g.anchor.y) <= radius + 20
  }
  return false
}

// ── Draw one object onto a canvas context ────────────────────
function drawObject(ctx, obj) {
  ctx.strokeStyle = obj.color
  ctx.fillStyle   = obj.color
  ctx.lineWidth   = obj.strokeWidth
  ctx.lineCap     = 'round'
  ctx.lineJoin    = 'round'
  const g = obj.geometry

  if (g.kind === 'freehand') {
    if (g.points.length === 0) return
    ctx.beginPath()
    ctx.moveTo(g.points[0].x, g.points[0].y)
    for (let i = 1; i < g.points.length; i++) ctx.lineTo(g.points[i].x, g.points[i].y)
    ctx.stroke()
    return
  }

  if (g.kind === 'shape') {
    const { start, end, filled } = g
    const x = Math.min(start.x, end.x), y = Math.min(start.y, end.y)
    const w = Math.abs(end.x - start.x), h = Math.abs(end.y - start.y)
    if (obj.tool === 'rect') {
      if (filled) ctx.fillRect(x, y, w, h); else ctx.strokeRect(x, y, w, h)
    } else if (obj.tool === 'circle') {
      ctx.beginPath()
      ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2)
      if (filled) ctx.fill(); else ctx.stroke()
    } else if (obj.tool === 'line') {
      ctx.beginPath(); ctx.moveTo(start.x, start.y); ctx.lineTo(end.x, end.y); ctx.stroke()
    } else if (obj.tool === 'arrow') {
      ctx.beginPath(); ctx.moveTo(start.x, start.y); ctx.lineTo(end.x, end.y); ctx.stroke()
      const angle = Math.atan2(end.y - start.y, end.x - start.x)
      const headLen = 10 + obj.strokeWidth * 2
      const a = Math.PI / 7
      ctx.beginPath()
      ctx.moveTo(end.x, end.y)
      ctx.lineTo(end.x - headLen * Math.cos(angle - a), end.y - headLen * Math.sin(angle - a))
      ctx.moveTo(end.x, end.y)
      ctx.lineTo(end.x - headLen * Math.cos(angle + a), end.y - headLen * Math.sin(angle + a))
      ctx.stroke()
    }
    return
  }

  if (g.kind === 'text') {
    const size   = 12 + obj.strokeWidth * 2
    const family = g.fontFamily === 'serif' ? 'Georgia, serif'
      : g.fontFamily === 'mono' ? 'monospace'
      : 'system-ui, sans-serif'
    ctx.font = `${size}px ${family}`
    ctx.textBaseline = 'top'
    ctx.fillText(g.content, g.anchor.x, g.anchor.y)
  }
}

// ── Tab bar styles ───────────────────────────────────────────
const tabBarStyle = {
  display: 'flex', alignItems: 'flex-end', gap: 3,
  padding: '0 12px', borderTop: '1px solid #d0d0d0',
  backgroundColor: '#ebebeb', overflowX: 'auto',
  flexShrink: 0, minHeight: 38,
}

const tabStyle = (isActive) => ({
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '7px 14px 6px',
  backgroundColor: isActive ? '#ffffff' : '#d4d4d4',
  border: '1px solid #bbb',
  borderBottom: isActive ? '2px solid #ffffff' : '1px solid #bbb',
  borderRadius: '6px 6px 0 0',
  cursor: 'pointer', fontSize: 12,
  fontWeight: isActive ? 600 : 400,
  color: isActive ? '#1a1a1a' : '#555',
  whiteSpace: 'nowrap', userSelect: 'none',
  transition: 'background-color 0.12s',
  minWidth: 72, justifyContent: 'space-between',
})

const addTabStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '6px 12px 5px',
  backgroundColor: 'transparent',
  border: '1px dashed #aaa', borderBottom: 'none',
  borderRadius: '6px 6px 0 0',
  cursor: 'pointer', fontSize: 18, fontWeight: 300,
  color: '#777', userSelect: 'none', minWidth: 36,
}

const deleteTabStyle = {
  fontSize: 11, color: '#aaa', lineHeight: 1,
  padding: '3px 5px', borderRadius: 4,
  cursor: 'pointer', flexShrink: 0,
  minWidth: 18, textAlign: 'center',
}

// ── Main component ───────────────────────────────────────────
export default function Whiteboard({ wb, call, onMinimize, timeLimitSeconds, callStartedAtMs }) {

  // ── Canvas refs ────────────────────────────────────────────
  const baseCanvasRef   = useRef(null)
  const activeCanvasRef = useRef(null)
  const containerRef    = useRef(null)

  // ── Size state ─────────────────────────────────────────────
  const [size, setSize]     = useState({ width: 0, height: 0 })
  const sizeRef             = useRef({ width: 0, height: 0 })
  const prevSizeRef         = useRef({ width: 0, height: 0 })

  // ── Tool state ─────────────────────────────────────────────
  const [activeTool,   setActiveTool]   = useState('pen')
  const [activeColor,  setActiveColor]  = useState('#1A1A1A')
  const [strokeWidth,  setStrokeWidth]  = useState(4)
  const [filled,       setFilled]       = useState(false)
  const [handRaised,   setHandRaised]   = useState(false)
  const [textModal,    setTextModal]    = useState(null)
  const [textDraft,    setTextDraft]    = useState('')

  // ── Export state ───────────────────────────────────────────
  const [exporting,  setExporting]  = useState(false)
  const [exportUrl,  setExportUrl]  = useState(null)
  // FIX: previously the Save PDF button used `!!exportUrl` to
  // disable itself, which meant that after the very first
  // successful save the button stayed disabled ("✓ Saved") for
  // the rest of the session — there was no way to save again even
  // after drawing more content. justSaved is a short-lived flag
  // purely for the "✓ Saved" label; it no longer blocks re-saving.
  const [justSaved,  setJustSaved]  = useState(false)

  // ── Page delete confirmation ───────────────────────────────
  const [confirmDeletePage, setConfirmDeletePage] = useState(null) // null | pageIndex

  // ── Takeover alert ─────────────────────────────────────────
  // Fires once when Person B tries to act while Person A was recently
  // active. Purely informational — never blocks anyone. Host is exempt.
  const [takeoverAlert,     setTakeoverAlert]     = useState(false)
  const pendingActionRef    = useRef(null)        // stored action to run after confirm
  const lastActiveIdentityRef = useRef(null)      // who was last active
  const lastActiveAtRef     = useRef(0)           // when they were last active (ms)
  const takeoverConfirmedRef  = useRef(false)     // already confirmed this session

  // ── Tool refs (avoid stale closures in pointer handlers) ───
  const activeToolRef   = useRef(activeTool)
  const activeColorRef  = useRef(activeColor)
  const strokeWidthRef  = useRef(strokeWidth)
  const filledRef       = useRef(filled)
  useEffect(() => { activeToolRef.current  = activeTool  }, [activeTool])
  useEffect(() => { activeColorRef.current = activeColor }, [activeColor])
  useEffect(() => { strokeWidthRef.current = strokeWidth }, [strokeWidth])
  useEffect(() => { filledRef.current      = filled      }, [filled])

  // ── Pan / drawing refs ─────────────────────────────────────
  const panRef            = useRef({ x: 0, y: 0 })
  const [pan, setPan]     = useState({ x: 0, y: 0 })
  const activePointersRef = useRef(new Map())
  const lastPanMidRef     = useRef(null)
  // True only while a REMOTE pan frame is being applied, so the pan
  // handler below does not rebroadcast it straight back.
  const applyingRemotePanRef = useRef(false)
  const drawingRef        = useRef(null)
  const erasedRef         = useRef(new Set())
  const historyRef        = useRef([])

  // ── rAF refs ───────────────────────────────────────────────
  const pendingActiveDrawRef = useRef(null)
  const rafScheduledRef      = useRef(false)

  // ── Current page and its objects ───────────────────────────
  const currentPage    = wb.pages[wb.currentPageIndex]
  const pageObjectsRef = useRef([])
  const pageObjects    = useMemo(() => {
    const filtered = wb.objects.filter((o) => o.pageId === currentPage?.id)
    pageObjectsRef.current = filtered
    return filtered
  }, [wb.objects, currentPage?.id])

  // ── Activity tracking helpers ──────────────────────────────
  // Record who was last active. When the active person changes,
  // reset the confirmed flag so the next different person is warned again.
  const recordActivity = useCallback((identity) => {
    if (lastActiveIdentityRef.current !== identity) {
      takeoverConfirmedRef.current = false
    }
    lastActiveIdentityRef.current = identity
    lastActiveAtRef.current = Date.now()
  }, [])

  // Returns true if this user should see the takeover warning.
  // Host (expert) is always exempt.
  const needsTakeoverAlert = useCallback(() => {
    const myIdentity = wb.identity
    const isHost     = myIdentity?.startsWith('expert-')
    if (isHost) return false
    if (takeoverConfirmedRef.current) return false
    const lastIdentity = lastActiveIdentityRef.current
    if (!lastIdentity || lastIdentity === myIdentity) return false
    if (Date.now() - lastActiveAtRef.current > ACTIVE_TIMEOUT_MS) return false
    return true
  }, [wb.identity])

  // ── Canvas drawing ─────────────────────────────────────────
  const drawActiveLayer = useCallback(() => {
    rafScheduledRef.current = false
    const canvas = activeCanvasRef.current
    if (!canvas || sizeRef.current.width === 0) return
    const dpr = window.devicePixelRatio || 1
    const ctx  = canvas.getContext('2d')
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, sizeRef.current.width, sizeRef.current.height)
    const obj = pendingActiveDrawRef.current
    if (obj) {
      ctx.save()
      ctx.translate(panRef.current.x, panRef.current.y)
      drawObject(ctx, obj)
      ctx.restore()
    }
  }, [])

  const scheduleActiveDraw = useCallback((obj) => {
    pendingActiveDrawRef.current = obj
    if (!rafScheduledRef.current) {
      rafScheduledRef.current = true
      requestAnimationFrame(drawActiveLayer)
    }
  }, [drawActiveLayer])

  const clearActiveLayer = useCallback(() => {
    pendingActiveDrawRef.current = null
    const canvas = activeCanvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
  }, [])

  // ── Effects ────────────────────────────────────────────────

  // Remote previews → active layer (no React state, bypasses render)
  // Also records the remote person as active so the takeover alert fires.
  useEffect(() => {
    // ── Shared viewport ─────────────────────────────────────
    // The board synced strokes, pages and permissions but not where
    // anyone was LOOKING, so a two-finger pan moved only the panner's
    // screen and the other person had to find the same spot by hand.
    //
    // Written straight to panRef and then through the same rAF-guarded
    // setPan the local drag uses, so an incoming stream of frames costs
    // one re-render per frame at most, exactly like panning locally.
    //
    // No author check is needed: the server does not echo a broadcast
    // back to the socket that sent it, so anything arriving here came
    // from the other participant. applyingRemotePanRef still guards the
    // send side (see the pan handler) so applying a remote frame cannot
    // re-broadcast it and ping-pong the two clients.
    wb.client.setViewportCallback?.(({ scrollX, scrollY }) => {
      if (typeof scrollX !== 'number' || typeof scrollY !== 'number') return
      applyingRemotePanRef.current = true
      panRef.current = { x: scrollX, y: scrollY }
      if (!rafScheduledRef.current) {
        rafScheduledRef.current = true
        requestAnimationFrame(() => {
          rafScheduledRef.current = false
          setPan({ ...panRef.current })
          applyingRemotePanRef.current = false
        })
      } else {
        applyingRemotePanRef.current = false
      }
    })

    wb.client.setPreviewCallback?.((previewObj) => {
      if (previewObj.pageId !== currentPage?.id) return
      if (previewObj.authorId && previewObj.authorId !== wb.identity) {
        recordActivity(previewObj.authorId)
      }
      scheduleActiveDraw(previewObj)
    })
  }, [wb.client, currentPage?.id, wb.identity, recordActivity, scheduleActiveDraw])

  // Reset pan when page changes — each page starts at origin.
  // Local-only on purpose: page:switch is already broadcast, and every
  // client runs this same reset when it arrives, so both sides land at
  // the origin together without a second message.
  useEffect(() => {
    panRef.current = { x: 0, y: 0 }
    setPan({ x: 0, y: 0 })
    clearActiveLayer()
  }, [wb.currentPageIndex, clearActiveLayer])

  // Resize observer
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect()
      const w = Math.round(rect.width)
      const h = Math.round(rect.height)
      sizeRef.current = { width: w, height: h }
      setSize({ width: w, height: h })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Resize canvas backing stores
  useEffect(() => {
    const base   = baseCanvasRef.current
    const active = activeCanvasRef.current
    if (!base || !active || size.width === 0) return
    const prev = prevSizeRef.current
    if (prev.width === size.width && prev.height === size.height) return
    prevSizeRef.current = size
    const dpr = window.devicePixelRatio || 1
    base.width    = size.width  * dpr
    base.height   = size.height * dpr
    active.width  = size.width  * dpr
    active.height = size.height * dpr
  }, [size])

  // Redraw base layer when objects/size/pan/page changes.
  // Explicitly clears when currentPage is undefined (new page not
  // yet synced) so previous page content doesn't linger.
  useEffect(() => {
    console.log('[wb] redraw effect firing', {
      pageObjectCount: pageObjects.length,
      pageObjectIds: pageObjects.map((o) => o.id),
      currentPageId: currentPage?.id,
      size,
    })
    const canvas = baseCanvasRef.current
    if (!canvas || size.width === 0) {
      console.log('[wb] redraw effect -> bailed (no canvas or zero size)')
      return
    }
    const dpr = window.devicePixelRatio || 1
    const ctx  = canvas.getContext('2d')
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, size.width, size.height)
    if (!currentPage) return
    ctx.save()
    ctx.translate(pan.x, pan.y)
    for (const obj of pageObjects) drawObject(ctx, obj)
    ctx.restore()
  }, [pageObjects, size, pan, currentPage])

  // ── Pointer helpers ────────────────────────────────────────
  const getPoint = (e) => {
    const rect = baseCanvasRef.current.getBoundingClientRect()
    return {
      x: e.clientX - rect.left - panRef.current.x,
      y: e.clientY - rect.top  - panRef.current.y,
    }
  }

  // ── Eraser ─────────────────────────────────────────────────
  const eraseAt = useCallback((p) => {
    const radius = ERASE_RADIUS_BASE + strokeWidthRef.current
    // BUG FIX — RACE-FREE HIT TESTING: previously iterated
    // pageObjectsRef.current, which only updates when React
    // re-renders and its useMemo recomputes from wb.objects. Any
    // object committed just before this call — whether it was YOUR
    // own stroke (already patched separately at commit time) or one
    // that just arrived from the OTHER participant over the socket
    // (never patched) — could still be missing from that ref for one
    // render cycle. Erasing inside that window silently found nothing:
    // no _localDelete, no deleteObject sent, and the stroke stayed on
    // the other side forever. This is what caused "first stroke
    // doesn't erase" and, after the earlier partial fix, resurfaced as
    // "last stroke doesn't erase" (the last stroke drawn — by either
    // side — is exactly the one most likely to still be in that gap).
    // getObjectsSnapshot() reads the client's live objectsById Map
    // directly, so it's always current regardless of render timing —
    // no dependency on React having caught up yet.
    const usedLiveSnapshot = typeof wb.client.getObjectsSnapshot === 'function'
    const liveObjects = (wb.client.getObjectsSnapshot?.() ?? pageObjectsRef.current)
      .filter((o) => o.pageId === currentPage?.id)
    console.log('[wb] eraseAt', {
      point: p, radius, usedLiveSnapshot,
      candidateCount: liveObjects.length,
      candidateIds: liveObjects.map((o) => o.id),
    })
    let hitCount = 0
    for (const obj of liveObjects) {
      if (erasedRef.current.has(obj.id)) continue
      if (objectNearPoint(obj, p, radius)) {
        hitCount += 1
        console.log('[wb] eraseAt -> hit, deleting', { id: obj.id, tool: obj.tool })
        erasedRef.current.add(obj.id)
        wb.client._localDelete?.(obj.id)
        wb.client.deleteObject(obj.id)
        if (wb.identity) recordActivity(wb.identity)
      }
    }
    if (hitCount === 0) {
      console.log('[wb] eraseAt -> no match', { point: p, radius })
    }
  }, [wb.client, wb.identity, recordActivity, currentPage?.id])


  // ── startDrawing — extracted so post-confirm path can call it too
  const startDrawing = useCallback((p) => {
    if (activeToolRef.current === 'text') {
      setTextDraft('')
      setTextModal(p)
      return
    }
    if (activeToolRef.current === 'eraser') {
      erasedRef.current = new Set()
      eraseAt(p)
      drawingRef.current = { eraser: true }
      return
    }
    const id = newId()
    if (activeToolRef.current === 'pen') {
      drawingRef.current = { id, kind: 'freehand', points: [p] }
      console.log('[wb] DOWN -> started freehand stroke', { id, point: p })
    } else {
      drawingRef.current = { id, kind: 'shape', start: p, end: p }
    }
  }, [eraseAt])

  // ── Pointer event handlers ─────────────────────────────────
  const handlePointerDown = useCallback((e) => {
    console.log('[wb] DOWN', {
      type: e.pointerType, id: e.pointerId,
      buttons: e.buttons, pressure: e.pressure,
      canDraw: wb.canDraw, tool: activeToolRef.current,
      activePointers: activePointersRef.current.size,
    })
    activePointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

    // 2-finger pan
    if (activePointersRef.current.size === 2) {
      console.log('[wb] DOWN -> 2-finger pan, aborting draw')
      drawingRef.current = null
      clearActiveLayer()
      const pts = Array.from(activePointersRef.current.values())
      lastPanMidRef.current = {
        x: (pts[0].x + pts[1].x) / 2,
        y: (pts[0].y + pts[1].y) / 2,
      }
      return
    }

    if (!wb.canDraw || !currentPage) return

    // Takeover alert — warn once if the other person was recently active.
    // Host (expert) is always exempt. After confirming, user works freely
    // until the other person becomes active again.
    if (needsTakeoverAlert()) {
      const otherLabel = lastActiveIdentityRef.current?.startsWith('expert-')
        ? 'the expert' : 'the customer'
      // Capture the point now so we can replay the action after confirm
      const capturedPoint = {
        x: e.clientX - baseCanvasRef.current.getBoundingClientRect().left - panRef.current.x,
        y: e.clientY - baseCanvasRef.current.getBoundingClientRect().top  - panRef.current.y,
      }
      pendingActionRef.current = { otherLabel, point: capturedPoint }
      setTakeoverAlert(true)
      return
    }

    activeCanvasRef.current.setPointerCapture(e.pointerId)
    startDrawing(getPoint(e))
  }, [wb.canDraw, currentPage, needsTakeoverAlert, startDrawing, clearActiveLayer])

  const handlePointerMove = useCallback((e) => {
    activePointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

    // 2-finger pan
    if (activePointersRef.current.size === 2) {
      const pts = Array.from(activePointersRef.current.values())
      const mid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 }
      if (lastPanMidRef.current) {
        const dx = mid.x - lastPanMidRef.current.x
        const dy = mid.y - lastPanMidRef.current.y
        panRef.current = { x: panRef.current.x + dx, y: panRef.current.y + dy }
        // Tell the other side. Sent inside the same rAF that repaints
        // locally, so this is naturally capped at one message per
        // frame instead of one per pointermove event.
        if (!rafScheduledRef.current) {
          rafScheduledRef.current = true
          requestAnimationFrame(() => {
            rafScheduledRef.current = false
            setPan({ ...panRef.current })
            if (!applyingRemotePanRef.current) {
              wb.client?.sendViewport?.({
                scrollX: panRef.current.x,
                scrollY: panRef.current.y,
                zoom:    1,
              })
            }
          })
        }
      }
      lastPanMidRef.current = mid
      return
    }

    if (!drawingRef.current || !wb.canDraw) return
    if (drawingRef.current.eraser) { eraseAt(getPoint(e)); return }

    const p = getPoint(e)
    const d = drawingRef.current
    let geometry

    if (d.kind === 'freehand') {
      d.points.push(p)
      geometry = { kind: 'freehand', points: d.points }
    } else {
      d.end = p
      geometry = { kind: 'shape', start: d.start, end: d.end, filled: filledRef.current }
    }

    wb.client.previewObject(d.id, currentPage.id, activeToolRef.current, activeColorRef.current, strokeWidthRef.current, geometry)
    scheduleActiveDraw({
      color: activeColorRef.current,
      strokeWidth: strokeWidthRef.current,
      tool: activeToolRef.current,
      geometry,
    })
  }, [wb.canDraw, wb.client, currentPage, eraseAt, scheduleActiveDraw])

  const handlePointerUp = useCallback((e) => {
    console.log('[wb] UP', { type: e.pointerType, id: e.pointerId, hasDrawing: !!drawingRef.current })
    activePointersRef.current.delete(e.pointerId)
    lastPanMidRef.current = null

    const d = drawingRef.current
    drawingRef.current = null
    clearActiveLayer()

    if (!d || d.eraser || !currentPage) {
      console.log('[wb] UP -> nothing to commit', { hadDrawing: !!d, isEraser: d?.eraser, hasCurrentPage: !!currentPage })
      return
    }

    let geometry
    if (d.kind === 'freehand') {
      if (d.points.length < 2) {
        console.log('[wb] UP -> stroke too short, discarded')
        return
      }
      geometry = { kind: 'freehand', points: d.points }
    } else {
      const moved = Math.hypot(d.end.x - d.start.x, d.end.y - d.start.y) > 2
      if (!moved) return
      geometry = { kind: 'shape', start: d.start, end: d.end, filled: filledRef.current }
    }

    console.log('[wb] UP -> committing', { id: d.id, pointCount: d.kind === 'freehand' ? d.points.length : 2 })
    wb.client.commitObject(d.id, currentPage.id, activeToolRef.current, activeColorRef.current, strokeWidthRef.current, geometry)
    historyRef.current.push(d.id)
    wb.client._localCommit?.({
      id: d.id, pageId: currentPage.id, authorId: 'local',
      tool: activeToolRef.current, color: activeColorRef.current,
      strokeWidth: strokeWidthRef.current, geometry, __preview: false,
    })
    // Record that this user was the last active on this page
    if (wb.identity) recordActivity(wb.identity)
  }, [wb.client, currentPage, clearActiveLayer, recordActivity, wb.identity])

  const confirmText = useCallback(() => {
    if (!textModal || !textDraft.trim() || !currentPage) { setTextModal(null); return }
    const id       = newId()
    const geometry = { kind: 'text', anchor: textModal, content: textDraft.trim(), fontFamily: 'sans' }
    wb.client.commitObject(id, currentPage.id, 'text', activeColorRef.current, strokeWidthRef.current, geometry)
    historyRef.current.push(id)
    wb.client._localCommit?.({
      id, pageId: currentPage.id, authorId: 'local',
      tool: 'text', color: activeColorRef.current,
      strokeWidth: strokeWidthRef.current, geometry, __preview: false,
    })
    if (wb.identity) recordActivity(wb.identity)
    setTextModal(null)
  }, [wb.client, currentPage, textModal, textDraft, recordActivity, wb.identity])

  const undo = useCallback(() => {
    const id = historyRef.current.pop()
    if (id) {
      wb.client._localDelete?.(id)
      wb.client.deleteObject(id)
    }
  }, [wb.client])

  const toggleHand = useCallback(() => {
    if (handRaised) { wb.client.lowerHand(); setHandRaised(false) }
    else            { wb.client.raiseHand(); setHandRaised(true)  }
  }, [wb.client, handRaised])

  // ── PDF export ─────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true)
    try {
      console.log('[wb] export -> rendering PDF', {
        pageCount: wb.pages.length, objectCount: wb.objects.length,
      })
      // BUG FIX: exportWhiteboardToPdf is now async (it loads
      // background images before drawing them). Must be awaited —
      // previously this line returned a Promise, which the FormData
      // in uploadWhiteboardExport silently serialised as "[object Promise]"
      // and the server's magic-bytes PDF check rejected it with a 400.
      const blob = await exportWhiteboardToPdf(wb.pages, wb.objects, wb.taskTitle, wb.otherPartyName)
      console.log('[wb] export -> PDF rendered', { blobSize: blob.size, blobType: blob.type })

      console.log('[wb] export -> uploading', { taskId: wb.taskId, applicationId: wb.applicationId })
      const result = await uploadWhiteboardExport({
        apiBaseUrl:    wb.apiBaseUrl,
        taskId:        wb.taskId,
        idToken:       wb.idToken,
        appCheckToken: wb.appCheckToken,
        applicationId: wb.applicationId,
        blob,
      })
      console.log('[wb] export -> upload succeeded', { url: result.url })
      setExportUrl(result.url)
      // FIX: previously exportUrl alone disabled the button forever.
      // justSaved gives the "✓ Saved" confirmation for a few seconds,
      // then reverts to "Save PDF" so later edits can be saved too.
      setJustSaved(true)
      setTimeout(() => setJustSaved(false), 3000)
      // NO a.click() — inside a WebView link clicks trigger page
      // navigation, tearing down the WS and ending the call.
    } catch (err) {
      // BUG FIX — GENERIC ALERT MASKED THE REAL CAUSE: this used to
      // show the same "Could not save the board" message for every
      // failure — an expired auth token (401, since wb.idToken is
      // fetched once at whiteboard-open time and never refreshed for
      // a long call), a tainted-canvas SecurityError from a
      // cross-origin worksheet background image, a rejected file
      // type, or a plain network failure all looked identical to the
      // user. console.error alone isn't enough since nobody's
      // watching devtools when it happens. Surface what actually
      // went wrong so it's visible in the RN app's forwarded logs
      // and, where possible, give the user an actionable message.
      console.error('[wb] export -> FAILED', {
        message: err?.message, name: err?.name, status: err?.status, stack: err?.stack,
      })
      // An App Check refusal is not an expired login, and its technical wording
      // must never be shown. The phone app also sends a plain sentence for it.
      const isAppCheck = err?.isAppCheck || isAppCheckText(err?.message) || /couldn't save that just now/i.test(err?.message || '')
      const isAuthError = err?.status === 401 || /401|unauthorized/i.test(err?.message || '')
      alert(
        err?.isTimeout
          ? err.message
          : isAppCheck
            ? FRIENDLY_TRY_AGAIN
            : isAuthError
            ? 'Your session has expired. Please rejoin the call and try saving again.'
            : `Could not save the board: ${err?.message || 'unknown error'}`
      )
    } finally {
      setExporting(false)
    }
  }

  const showFillToggle = activeTool === 'rect' || activeTool === 'circle'

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="wb-overlay">

      {/* Header */}
      <div className="wb-header">
        <span>Whiteboard</span>
        <SessionTimer timeLimitSeconds={timeLimitSeconds} callStartedAtMs={callStartedAtMs} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {(pan.x !== 0 || pan.y !== 0) && (
            <button
              onClick={() => { panRef.current = { x: 0, y: 0 }; setPan({ x: 0, y: 0 }) }}
              title="Reset view to origin"
              style={{
                background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: 6, color: '#fff', fontSize: 13, padding: '3px 8px',
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              ⊙ Reset view
            </button>
          )}
          <button
            className="wb-save-btn"
            onClick={handleExport}
            disabled={exporting}
            title="Save as PDF"
          >
            {exporting ? 'Saving...' : justSaved ? '✓ Saved' : '⬇️ Save PDF'}
          </button>
          <button className="wb-close" onClick={onMinimize} title="Minimize">─</button>
        </div>
      </div>

      {/* Mini video tiles */}
      {call && (call.localStream || call.remoteStream) && (
        <div className="wb-video-row">
          {call.remoteStream && <MiniVideoTile stream={call.remoteStream} label="Them" />}
          {call.cameraEnabled && call.localStream && (
            <MiniVideoTile stream={call.localStream} mirrored label="You" />
          )}
        </div>
      )}

      {/* Page tab bar — Excel style */}
      {wb.pages.length > 0 && (
        <div style={tabBarStyle}>
          {wb.pages.map((p, i) => {
            const isActive = i === wb.currentPageIndex
            return (
              <div
                key={p.id}
                style={tabStyle(isActive)}
                onClick={() => { if (i !== wb.currentPageIndex) wb.client.switchPage(i) }}
              >
                <span>Page {i + 1}</span>
                {wb.pages.length > 1 && (
                  <span
                    style={deleteTabStyle}
                    title={`Delete page ${i + 1}`}
                    onClick={(e) => { e.stopPropagation(); setConfirmDeletePage(i) }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#c00'; e.currentTarget.style.backgroundColor = '#fee' }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#aaa'; e.currentTarget.style.backgroundColor = 'transparent' }}
                  >
                    ✕
                  </span>
                )}
              </div>
            )
          })}
          {wb.connectionState === 'connected' && (
            <div
              style={addTabStyle}
              title="Add page"
              onClick={() => wb.client.addPage()}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#ddd'; e.currentTarget.style.color = '#333' }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#777' }}
            >
              +
            </div>
          )}
        </div>
      )}

      {/* Draw permission bar */}
      {wb.drawMode === 'raised_hand' && !wb.canDraw && (
        <div className="wb-permbar">
          <button className="wb-raisehand" onClick={toggleHand} disabled={handRaised}>
            {handRaised ? '✋ Hand raised — waiting for host' : '✋ Raise hand to draw'}
          </button>
        </div>
      )}

      {wb.deniedReason && <div className="wb-denied">{wb.deniedReason}</div>}

      {/* Canvas area */}
      <div className="wb-canvaswrap" ref={containerRef} style={{ position: 'relative' }}>
        {wb.connectionState === 'connecting' ? (
          <div className="wb-center">Connecting to whiteboard...</div>
        ) : wb.connectionState === 'failed' ? (
          <div className="wb-center">Couldn't connect. The call itself is unaffected.</div>
        ) : (
          <>
            <canvas
              ref={baseCanvasRef}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
            />
            <canvas
              ref={activeCanvasRef}
              style={{
                position: 'absolute', inset: 0, width: '100%', height: '100%',
                touchAction: 'none',
                cursor: !wb.canDraw ? 'default'
                  : activeTool === 'eraser' ? 'cell'
                  : 'crosshair',
                // Red border when eraser active — visually unmistakable
                outline: activeTool === 'eraser' && wb.canDraw
                  ? '3px solid rgba(226,75,74,0.6)' : 'none',
              }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onPointerLeave={handlePointerUp}
            />
          </>
        )}
      </div>

      {/* Toolbar */}
      {wb.canDraw ? (
        <div className="wb-toolbar">
          <div className="wb-tools">
            {TOOLS.map((t) => (
              <button
                key={t.id}
                className={`wb-tool ${activeTool === t.id ? 'active' : ''}`}
                style={activeTool === t.id && t.id === 'eraser'
                  ? { backgroundColor: 'rgba(226,75,74,0.25)', border: '1px solid #e24b4a' }
                  : undefined}
                onClick={() => setActiveTool(t.id)}
              >
                {t.icon}
              </button>
            ))}
            <button className="wb-tool" onClick={undo}>↩️</button>
          </div>
          <div className="wb-controls">
            <div className="wb-colors">
              {COLORS.map((c) => (
                <button
                  key={c}
                  className={`wb-swatch ${activeColor === c ? 'active' : ''}`}
                  style={{ background: c }}
                  onClick={() => setActiveColor(c)}
                />
              ))}
            </div>
            <div className="wb-widths">
              {WIDTHS.map((w) => (
                <button
                  key={w}
                  className={`wb-width ${strokeWidth === w ? 'active' : ''}`}
                  onClick={() => setStrokeWidth(w)}
                >
                  <span style={{
                    width: w * 2, height: w * 2, borderRadius: w,
                    background: '#1A1A1A', display: 'block',
                  }} />
                </button>
              ))}
            </div>
            {showFillToggle && (
              <button
                className={`wb-fill ${filled ? 'active' : ''}`}
                onClick={() => setFilled((f) => !f)}
              >
                {filled ? '⬛ Filled' : '⬜ Outline'}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="wb-disabled">You don't currently have draw permission</div>
      )}

      {/* ── Modals ─────────────────────────────────────────── */}

      {/* Takeover alert — courtesy warning, not a permission block */}
      {takeoverAlert && (
        <div
          className="wb-modal-overlay"
          onClick={() => { setTakeoverAlert(false); pendingActionRef.current = null }}
        >
          <div className="wb-modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 32, textAlign: 'center', marginBottom: 8 }}>✋</div>
            <h4 style={{ textAlign: 'center', marginBottom: 6 }}>Heads up!</h4>
            <p style={{ fontSize: 13, color: '#555', textAlign: 'center', margin: '0 0 18px', lineHeight: 1.5 }}>
              <strong>
                {pendingActionRef.current?.otherLabel === 'the expert' ? 'The expert' : 'The customer'}
              </strong>{' '}
              was recently active on this page.<br />
              Do you want to continue?
            </p>
            <div className="wb-modal-row">
              <button
                className="wb-modal-cancel"
                onClick={() => { setTakeoverAlert(false); pendingActionRef.current = null }}
              >
                Cancel
              </button>
              <button
                className="wb-modal-confirm"
                onClick={() => {
                  takeoverConfirmedRef.current = true
                  const pending = pendingActionRef.current
                  pendingActionRef.current = null
                  setTakeoverAlert(false)
                  // Replay the drawing action the user started
                  if (pending?.point) startDrawing(pending.point)
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Text input modal */}
      {textModal && (
        <div className="wb-modal-overlay" onClick={() => setTextModal(null)}>
          <div className="wb-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Add text</h4>
            <textarea
              autoFocus
              value={textDraft}
              onChange={(e) => setTextDraft(e.target.value)}
              placeholder="Type something..."
            />
            <div className="wb-modal-row">
              <button className="wb-modal-cancel" onClick={() => setTextModal(null)}>Cancel</button>
              <button className="wb-modal-confirm" onClick={confirmText}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete page confirmation */}
      {confirmDeletePage !== null && (
        <div
          className="wb-modal-overlay"
          onClick={() => setConfirmDeletePage(null)}
        >
          <div className="wb-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Delete page {confirmDeletePage + 1}?</h4>
            <p style={{ fontSize: 13, color: '#666', margin: '4px 0 16px', lineHeight: 1.5 }}>
              All drawings on this page will be permanently removed for both participants.
            </p>
            <div className="wb-modal-row">
              <button className="wb-modal-cancel" onClick={() => setConfirmDeletePage(null)}>
                Cancel
              </button>
              <button
                className="wb-modal-confirm"
                style={{ backgroundColor: '#e24b4a' }}
                onClick={() => {
                  wb.client.deletePage(confirmDeletePage)
                  setConfirmDeletePage(null)
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}